/**
 * True/magnetic course display (spec 14C). Storage and all route/nav math
 * stay true throughout the app — this is the ONLY place a bearing is ever
 * converted, and it happens purely for display. Magnetic = true − declination
 * (normalized); when the setting is magnetic, a `mv` (misvisende) suffix is
 * appended so a screenshot is never ambiguous — true stays unsuffixed (°),
 * matching the app's existing default look.
 */
import { useSyncExternalStore } from 'react';
import { formatCog, normalizeBearing } from '@boatnav/core';
import { declinationStore } from './declinationStore.js';
import type { CourseRef } from '../../app/unitsStore.js';

export function useDeclinationDeg(): number | null {
  return useSyncExternalStore(
    declinationStore.subscribe,
    () => declinationStore.getState().declDeg,
    () => null,
  );
}

/**
 * Full formatted course string, e.g. "095°" (true) or "098°mv" (magnetic).
 * `trueDeg: null` (no COG yet) formats as "—" same as `formatCog`.
 */
export function formatCourseRef(
  trueDeg: number | null,
  declDeg: number | null,
  courseRef: CourseRef,
): string {
  if (trueDeg == null) return formatCog(null);
  const magnetic = courseRef === 'magnetic' && declDeg != null;
  const display = magnetic ? normalizeBearing(trueDeg - declDeg!) : trueDeg;
  return magnetic ? `${formatCog(display)}mv` : formatCog(display);
}
